/**
 * Backend bilan yagona aloqa nuqtasi.
 *
 * === Nega hammasi shu faylda ===
 *
 * Frontend alohida olib chiqilishi mumkin bo'lishi kerak (boshqa repo,
 * boshqa deploy, hatto boshqa backend). Shuning uchun `fetch` faqat shu
 * yerda chaqiriladi. Komponentlar HTTP ni umuman bilmaydi — ular
 * `sendMessage()` va `search()` ni ko'radi, xolos.
 *
 * Backend manzili almashsa — bitta o'zgaruvchi (`VITE_API_URL`).
 * Backend butunlay boshqasiga almashsa — faqat shu fayl qayta yoziladi.
 */

const BASE = import.meta.env.VITE_API_URL ?? "";

const SESSION_KEY = "ekspertiza.session";

export function getSessionId() {
  try {
    return localStorage.getItem(SESSION_KEY) || null;
  } catch {
    return null; // maxfiy rejimda localStorage o'chirilgan bo'lishi mumkin
  }
}

export function setSessionId(id) {
  try {
    if (id) localStorage.setItem(SESSION_KEY, id);
  } catch {
    /* jim */
  }
}

/** Suhbat id'si birinchi so'rovdan oldin: foto va matn bir vaqtda ketsa ham bitta suhbatga tushadi. */
export function ensureSession() {
  const sid = getSessionId() || uid().replace(/-/g, "");
  setSessionId(sid);
  return sid;
}

export function clearSession() {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    /* jim */
  }
}

async function json(path, options) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      detail = (await res.json()).detail ?? detail;
    } catch {
      /* javob JSON emas */
    }
    throw new Error(detail);
  }
  return res.json();
}

export const health = () => json("/api/health");
export const search = (q) =>
  json(`/api/search?q=${encodeURIComponent(q)}`);
export const codeInfo = (code) =>
  json(`/api/code/${encodeURIComponent(code)}`);

/**
 * Ekspert bahosi — javob to'g'rimi.
 *
 * `turn_id` `done` hodisasidan keladi. Bazasiz ishlaganda u `null`
 * bo'ladi va interfeys baho tugmalarini umuman ko'rsatmaydi.
 */
export const sendFeedback = (payload) =>
  json("/api/feedback", { method: "POST", body: JSON.stringify(payload) });

/** Dashboard: `days` — davr (0 hammasi, 1 bugun, N oxirgi N kun). */
export const reviewStats = ({ days = 0 } = {}) => json(`/api/review/stats?days=${days}`);

export function reviewTurns({ limit = 50, offset = 0, verdict = "",
                              unrated = false, q = "", days = 0 } = {}) {
  const p = new URLSearchParams({ limit, offset, days });
  if (verdict) p.set("verdict", verdict);
  if (unrated) p.set("unrated", "true");
  if (q) p.set("q", q);
  return json(`/api/review/turns?${p}`);
}

export const reviewSession = (id) =>
  json(`/api/review/session/${encodeURIComponent(id)}`);

/** Juftlik tekshiruvi (imtiyoz arizasi). `tests` — qo'lda sinov yozuvlari ham. */
export const verifyStats = ({ tests = false, days = 0 } = {}) =>
  json(`/api/verify/stats?days=${days}${tests ? "&tests=true" : ""}`);

export function verifyTurns({ limit = 25, offset = 0, q = "", kind = "", tests = false,
                              history = false, days = 0 } = {}) {
  const p = new URLSearchParams({ limit, offset, days });
  if (q) p.set("q", q);
  if (kind) p.set("kind", kind);
  if (tests) p.set("tests", "true");
  if (history) p.set("history", "true");
  return json(`/api/verify/turns?${p}`);
}

export const verifyTurn = (id) => json(`/api/verify/turns/${encodeURIComponent(id)}`);

/** Fotodan tasvirlar — barcha mijozlarniki, oxirgi baho bilan: `{stats, items}`. */
export const reviewVisuals = ({ days = 0, kind = "", limit = 24, offset = 0 } = {}) =>
  json(`/api/review/visuals?${new URLSearchParams({ days, kind, limit, offset })}`);

/** Savolnoma va kiritish: `{questionnaire, intake, items}`. */
export const reviewForms = ({ days = 0, kind = "", limit = 25, offset = 0 } = {}) =>
  json(`/api/review/forms?${new URLSearchParams({ days, kind, limit, offset })}`);

export const llmStatus = () => json("/api/llm_status");

/** Server bergan nisbiy yo'l (`/api/...`) -> to'liq manzil (frontend alohida deploy qilinsa ham). */
export const apiUrl = (path, params = {}) => {
  const u = new URL(`${BASE}${path}`, window.location.origin);
  Object.entries(params).forEach(([k, v]) => u.searchParams.set(k, String(v)));
  return u.toString();
};

/** Suhbatlar tarixi — barcha qurilmalardan (UI uchun, tashqi API emas). */
export const listChats = (limit = 50) => json(`/api/chats?limit=${limit}`).then((r) => r.items);

/** "Foto tahlillari" dan yashirilgan vazifalar va yashirish (UI uchun; `/api/v1/visuals` o'zgarmaydi). */
export const hiddenVisuals = () => json("/api/gallery/hidden").then((r) => r.items);
export const hideVisual = (id) => json(`/api/gallery/${encodeURIComponent(id)}`, { method: "DELETE" });
/** Tasvir yaratilgan suhbat id'si; bog'lanmagan bo'lsa `null`. */
export const visualChat = (id) =>
  json(`/api/gallery/${encodeURIComponent(id)}/chat`).then((r) => r.session_id, () => null);

/** Suhbatni chap paneldan o'chirish (yumshoq — serverda saqlanib qoladi). */
export const deleteChat = (id) => json(`/api/chats/${encodeURIComponent(id)}`, { method: "DELETE" });

/** Bitta suhbat: `{type: "turn", ...}` va `{type: "visual", job_id, hint}`, vaqt tartibida. */
export const getChat = (id) => json(`/api/chats/${encodeURIComponent(id)}`).then((r) => r.items);

/** Tasvirni joriy suhbatga bog'laydi; suhbat yo'q bo'lsa server yaratadi va id beradi. */
export async function linkVisual(jobId, hint = "") {
  const r = await json("/api/chat/visual", {
    method: "POST",
    body: JSON.stringify({ job_id: jobId, session_id: getSessionId(), hint }),
  });
  setSessionId(r.session_id);
  return r.session_id;
}

export function resetChat() {
  const session_id = getSessionId();
  clearSession();
  return json("/api/reset", {
    method: "POST",
    body: JSON.stringify({ message: "", session_id }),
  });
}

/**
 * Suhbat — hodisalar oqimi.
 *
 * `EventSource` ishlatilmadi: u faqat GET yuboradi, bizga esa POST kerak
 * (savol uzun bo'lishi mumkin, URL ga sig'maydi). Shuning uchun `fetch`
 * + oqimni qo'lda o'qish. SSE formati o'zi oddiy: bo'sh qator bilan
 * ajratilgan `event:` / `data:` bloklari.
 *
 * @param {string} message
 * @param {(event: string, data: object) => void} onEvent
 * @param {AbortSignal} [signal]
 */
export async function sendMessage(message, onEvent, signal) {
  const res = await fetch(`${BASE}/api/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message, session_id: getSessionId() }),
    signal,
  });

  if (!res.ok) {
    let detail = `xato ${res.status}`;
    try {
      detail = (await res.json()).detail ?? detail;
    } catch {
      /* javob JSON emas */
    }
    throw new Error(detail);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Bloklar bo'sh qator bilan ajratiladi. Oxirgi (tugallanmagan)
    // bo'lak buferda qoladi — keyingi o'qishda davom etadi.
    const blocks = buffer.split("\n\n");
    buffer = blocks.pop() ?? "";

    for (const block of blocks) {
      let name = "message";
      const lines = [];
      for (const line of block.split("\n")) {
        if (line.startsWith("event:")) name = line.slice(6).trim();
        else if (line.startsWith("data:")) lines.push(line.slice(5).trim());
      }
      if (!lines.length) continue;
      try {
        onEvent(name, JSON.parse(lines.join("\n")));
      } catch {
        /* noto'g'ri blok — tashlab ketamiz, oqim to'xtamasin */
      }
    }
  }
}

// --- Fotodan tovar tasviri: universal `/api/v1/visuals` (docs/api/vizual.md) ---
//
// UI bu API'ning oddiy mijozi: boshqa platformalar ham aynan shu
// chaqiruvlardan foydalanadi. Xato shakli: {"error": {"code", "message"}}.

async function v1(path, options = {}) {
  const res = await fetch(`${BASE}/api/v1/visuals${path}`, options);
  if (res.status === 204) return null;
  let body = null;
  try {
    body = await res.json();
  } catch {
    /* javob JSON emas */
  }
  if (!res.ok) {
    const err = new Error(body?.error?.message ?? `xato ${res.status}`);
    err.code = body?.error?.code;
    throw err;
  }
  return body;
}

// `crypto.randomUUID` faqat https yoki localhost da bor: mahalliy tarmoqdan
// (http://192.168...) ochilganda u yo'q va chaqiruv yiqiladi
export const uid = () =>
  crypto.randomUUID?.() ??
  Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");

const PHOTO_SIDE = 2048;
const THUMB_SIDE = 320;

function drawJpeg(img, side, quality) {
  const k = Math.min(1, side / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.naturalWidth * k);
  canvas.height = Math.round(img.naturalHeight * k);
  canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
  return new Promise((ok) =>
    canvas.toBlob((blob) => {
      canvas.width = canvas.height = 0;
      ok(blob);
    }, "image/jpeg", quality),
  );
}

/**
 * Foto biriktirilgan zahoti: yuklash uchun 2048 px JPEG va ko'rsatish
 * uchun 320 px eskiz. Telefon kamerasi fotosi (12 MP) to'liq o'lchamda
 * ko'rsatilsa har biri ~48 MB xotira egallaydi va telefon sahifani qayta
 * yuklab yuboradi. `<img>` orqali — EXIF burilishini hamma brauzer hisobga oladi.
 *
 * @returns {Promise<{file: File, thumb: string}>} `thumb` — object URL yoki ""
 */
export async function preparePhoto(file) {
  const src = URL.createObjectURL(file);
  const img = new Image();
  try {
    // `img.decode()` emas: sahifa ko'rinmay turganda (fayl tanlash oynasidan
    // qaytish paytida) u hech qachon tugamasligi mumkin, `onload` esa keladi
    await new Promise((ok, fail) => {
      img.onload = ok;
      img.onerror = fail;
      img.src = src;
    });
    // Har doim qayta siqiladi, o'lcham saqlanadi: serverga tarmoq 80–400 KB/s bo'lgan
    // paytda (2026-10-16, nginx `rt`) asl 1–2 MB fayl 5–25 s yuklanardi. Asli kichikroq bo'lsa — o'zi.
    const packed = await drawJpeg(img, PHOTO_SIDE, 0.85);
    const big = Math.max(img.naturalWidth, img.naturalHeight) > PHOTO_SIDE;
    const upload = packed && (big || packed.size < file.size) ? packed : null;
    const thumb = await drawJpeg(img, THUMB_SIDE, 0.8);
    return {
      file: upload ? new File([upload], "photo.jpg", { type: "image/jpeg" }) : file,
      thumb: thumb ? URL.createObjectURL(thumb) : "",
    };
  } catch {
    return { file, thumb: "" }; // brauzer o'qiy olmagan format (HEIC) — server o'zi hal qiladi
  } finally {
    img.src = "";
    URL.revokeObjectURL(src);
  }
}

/**
 * @param {File[]} photos 1-3 ta foto (`preparePhoto` dan o'tgan)
 * @param {{product_hint?: string, lang?: string, quality?: string, ref?: string}} fields
 */
export function createVisual(photos, fields = {}) {
  const form = new FormData();
  photos.forEach((p) => form.append("photos", p));
  Object.entries(fields).forEach(([k, v]) => v && form.append(k, v));
  return v1("", {
    method: "POST",
    body: form,
    // Tarmoq uzilib qayta yuborilsa ikkinchi vazifa (va to'lov) yaratilmaydi
    headers: { "Idempotency-Key": uid() },
  });
}

export const getVisual = (id) => v1(`/${encodeURIComponent(id)}`);

/** Barcha qurilmalardan yuborilgan tasvirlar (yangisi birinchi). */
export const listVisuals = (limit = 50) => v1(`?limit=${limit}`).then((r) => r.items);

/** Rasm yoki posterning `width` px enli nusxasi (server yaxlitlaydi va keshlaydi). */
export function sized(url, width) {
  const u = new URL(url, window.location.origin);
  u.searchParams.set("w", String(width));
  return u.toString();
}

/** Faqat tahlil qilingan vazifaga rasm va poster (tahlil qayta qilinmaydi). */
export const drawVisual = (id) => v1(`/${encodeURIComponent(id)}/draw`, { method: "POST" });

/** Yiqilgan vazifa qayta — server saqlangan fotolardan foydalanadi. */
export const retryVisual = (id) => v1(`/${encodeURIComponent(id)}/retry`, { method: "POST" });

export const sendVisualFeedback = (id, payload) =>
  v1(`/${encodeURIComponent(id)}/feedback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

export const visualStats = () => v1("/stats");

/**
 * Jarayon oqimi (SSE): `onStage({stage, product})`, oxirida `onDone(view)`.
 * `product` — tovar nomi, tahlil tugagach keladi.
 * @returns {() => void} oqimni yopish
 */
export function watchVisual(id, { onStage, onDone }) {
  let stopped = false;
  let timer = null;
  const es = new EventSource(`${BASE}/api/v1/visuals/${encodeURIComponent(id)}/events`);
  const finish = (view) => {
    stopped = true;
    es.close();
    onDone(view);
  };
  es.addEventListener("stage", (e) => onStage(JSON.parse(e.data)));
  es.addEventListener("done", (e) => finish(JSON.parse(e.data)));
  es.addEventListener("failed", (e) => finish(JSON.parse(e.data)));
  // Oqim uzilsa (proksi, tarmoq) — oddiy so'rov bilan holatni kuzatamiz
  const poll = async () => {
    if (stopped) return;
    try {
      const v = await getVisual(id);
      if (["done", "failed"].includes(v.status)) return finish(v);
      onStage({ stage: v.stage, product: v.product?.name });
    } catch {
      /* keyingi urinishda */
    }
    timer = setTimeout(poll, 3000);
  };
  es.onerror = () => {
    es.close();
    if (!stopped) poll();
  };
  return () => {
    stopped = true;
    es.close();
    clearTimeout(timer);
  };
}

/** Poster fayli (yuklab olish uchun); boshqa tildagisini server talab bo'yicha yig'adi. */
export async function fetchPoster(url, lang) {
  const res = await fetch(posterIn(url, lang));
  if (!res.ok) throw new Error(`Poster yuklanmadi (${res.status}), qayta urinib ko'ring`);
  return res.blob();
}

/** Boshqa tildagi poster: imzo fayl nomiga qo'yilgan, `lang` erkin almashadi. */
export function posterIn(url, lang, width = 0) {
  const u = new URL(url, window.location.origin);
  u.searchParams.set("lang", lang);
  if (width) u.searchParams.set("w", String(width));
  return u.toString();
}
